import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Drawer, styled, Typography } from '@mui/material';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { CollapsibleSection, LwButton, MultiDateCalendar } from 'redesign';
import { useCalendarDefaults } from 'shared/hooks/useCalendarDefaults';
import { dateTimeToDateObject } from 'shared/components/shift-forms/components/shift-multiply-calendar/multi-date-object-utils';
import { useState } from 'react';
import { IconCaretLeft } from 'assets/img';
import { TimeSlotWrapper } from './time-slot-wrapper';
import { SelectedDatesSection } from './selected-dates-section';
import { AvailabilityWorkersSelect } from './availability-workers-select';
import { SegmentsForEdit, TimeSlotFormContext } from './time-slot.types';
import { useHandleAvailabilitySubmission } from '../hooks/use-handle-availability-submission';
import { AvailabilityMode } from '../hooks/use-availability-action-handler';
import { useGetCompanyEmployees } from '../hooks/use-get-company-employees';
import { useGetWorkerAvailability } from '../hooks/use-get-worker-availability';
import { useGetAdditionalAvailabilities } from '../hooks/use-calendar-validation';
import { useCalendarMethods } from '../hooks/use-calendar-methods';
import { useAvailabilityEditWarnings } from '../hooks/use-availability-form-warnings';

export type AvailabilityForEdit = {
  selectedWorker: { id: string; name: string; description: string };
  availabilitySegments: SegmentsForEdit[];
  availabilityDate: DateTime;
};
export type SelectedDateWithAvailability = {
  date: DateTime;
  errored?: boolean;
  relatedAvailability?: { segmentId: string; startTime: DateTime; endTime: DateTime }[];
};

type AvailabilityFormProps = {
  availabilityForEdit: AvailabilityForEdit | null;
  onClose: () => void;
  isOpen: boolean;
  fromDate: DateTime;
  searchTerm: string | undefined;
  mode?: AvailabilityMode;
};

export const AvailabilitySidebarFormProvider = (props: AvailabilityFormProps) => {
  const { mode, availabilityForEdit } = props;
  const selectedWorker = availabilityForEdit?.selectedWorker;
  const availabilitySegments = availabilityForEdit?.availabilitySegments ?? [];
  const isCreateMode = mode === 'create';
  const formMethods = useForm<TimeSlotFormContext>({
    defaultValues: {
      workerOptions: isCreateMode ? [] : selectedWorker ? [selectedWorker] : [],
      workerSelect: isCreateMode ? '' : (selectedWorker?.id ?? ''),
      availability: isCreateMode
        ? [{ segmentId: '', utcStartDateTime: '', utcEndDateTime: '' }]
        : availabilitySegments.length > 0
          ? availabilitySegments
          : [],
    },
  });
  return (
    <FormProvider {...formMethods}>
      <AvailabilitySidebar {...props} />
    </FormProvider>
  );
};

const AvailabilitySidebar = ({
  availabilityForEdit,
  onClose,
  isOpen,
  fromDate,
  searchTerm,
  mode = 'edit',
}: AvailabilityFormProps) => {
  const isCreateMode = mode === 'create';
  const { t } = useTranslation('all-platforms-common');
  const { monthLabels, dayLabels } = useCalendarDefaults();
  const { workerOptions: companyEmployees } = useGetCompanyEmployees();
  const [selectedDates, setSelectedDates] = useState<SelectedDateWithAvailability[]>(
    isCreateMode
      ? [{ date: fromDate }]
      : availabilityForEdit?.availabilityDate
        ? [{ date: availabilityForEdit.availabilityDate }]
        : [{ date: fromDate }]
  );

  const [selectedFirstMonth, setSelectedFirstMonth] = useState<DateTime>(fromDate.startOf('month'));

  const availabilityDates = isCreateMode
    ? selectedDates.map((selectedDate) => selectedDate.date.set({ hour: 12 }))
    : [availabilityForEdit?.availabilityDate ?? fromDate];
  const selectedWorker = availabilityForEdit?.selectedWorker;
  const availabilitySegments = availabilityForEdit?.availabilitySegments ?? [];

  const { watch } = useFormContext<TimeSlotFormContext>();

  const selectedWorkerId = watch('workerSelect');

  const monthAvailabilityQuery = useGetWorkerAvailability(
    selectedWorkerId ?? null,
    selectedFirstMonth,
    selectedFirstMonth.plus({ months: 1 }).endOf('month')
  );

  const { availabilitiesForAllSelectedDays } = useGetAdditionalAvailabilities({
    selectedDates,
    setSelectedDates,
    monthAvailabilityQuery,
    selectedFirstMonth,
  });
  const { handleDateSelection, handleDayMapping } = useCalendarMethods({
    isCreateMode,
    selectedDates,
    setSelectedDates,
    monthAvailabilityQuery,
  });

  const { handleDeleteAll, handleSubmit } = useHandleAvailabilitySubmission({
    workerId: selectedWorker?.id,
    originalAvailability: isCreateMode
      ? (availabilitiesForAllSelectedDays ?? [])
      : availabilitySegments,
    availabilityDates,
    fromDate,
    searchTerm,
    cancel: onClose,
    mode,
  });

  const { availabilityHeaderWarning, workerSectionHeaderError } = useAvailabilityEditWarnings({
    differentDayAvailabilities: availabilitiesForAllSelectedDays ?? [],
  });

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose} PaperProps={{ style: { width: 600 } }}>
      <FormWrapper>
        <Box>
          <Typography variant="h1" mb={4}>
            {isCreateMode
              ? t('edit_availability.create_title') || 'Create Availability'
              : t('edit_availability.title')}
          </Typography>
        </Box>
        <Divider />
        <form onSubmit={handleSubmit} className="formhtml">
          <FormBody>
            <CollapsibleSection
              title={t('edit_availability.worker_section_title')}
              initializeAsOpen={isCreateMode}
              warningText={workerSectionHeaderError}
            >
              <AvailabilityWorkersSelect
                workerOptions={
                  isCreateMode ? companyEmployees : selectedWorker ? [selectedWorker] : []
                }
                disabled={!isCreateMode}
              />
            </CollapsibleSection>
            <CollapsibleSection
              title={t('edit_availability.availability_section_title')}
              warningText={availabilityHeaderWarning}
            >
              <CalendarWrapper data-testid="edit-availability-calendar">
                <MultiDateCalendar
                  value={dateTimeToDateObject(selectedFirstMonth)}
                  currentDate={dateTimeToDateObject(selectedFirstMonth)}
                  onChange={handleDateSelection}
                  onMonthChange={(newMonth) => {
                    setSelectedFirstMonth(DateTime.fromJSDate(newMonth.toDate()).startOf('month'));
                  }}
                  mapDays={handleDayMapping}
                  weekStartDayIndex={1}
                  multiple={false}
                  buttons={isCreateMode}
                  renderButton={CustomButtons}
                  disableMonthPicker={!isCreateMode}
                  disableYearPicker={!isCreateMode}
                  months={monthLabels}
                  weekDays={dayLabels}
                  numberOfMonths={2}
                />
              </CalendarWrapper>
              <SelectedDatesSection
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
              />
              <SectionDivider />
              <TimeSlotWrapper
                isCreateMode={isCreateMode}
                additionalAvailabilities={availabilitiesForAllSelectedDays}
              />
            </CollapsibleSection>
            <FormActionContainer>
              {!isCreateMode && (
                <LwButton
                  onClick={handleDeleteAll}
                  color="secondary"
                  title={t('edit_availability.delete_all')}
                >
                  {t('edit_availability.delete_all')}
                </LwButton>
              )}
              {isCreateMode && <Box />}
              <StandardActionContainer>
                <LwButton onClick={onClose} color="secondary" title={t('edit_availability.close')}>
                  {t('edit_availability.close')}
                </LwButton>
                <LwButton
                  color="primary"
                  type="submit"
                  title={
                    isCreateMode
                      ? t('edit_availability.create') || t('edit_availability.save')
                      : t('edit_availability.save')
                  }
                >
                  {isCreateMode
                    ? t('edit_availability.create') || t('edit_availability.save')
                    : t('edit_availability.save')}
                </LwButton>
              </StandardActionContainer>
            </FormActionContainer>
          </FormBody>
        </form>
      </FormWrapper>
    </Drawer>
  );
};

const FormWrapper = styled(Box)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(8),
  overflow: 'auto',
  '& .formhtml': {
    height: '95%',
  },
}));
const StandardActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));
const FormBody = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  width: '550px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const FormActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignContent: 'center',
  marginTop: 'auto',
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const CalendarWrapper = styled(Box)(({ theme }) => ({
  '&& .rmdp-week-day': {
    width: theme.spacing(8),
    height: theme.spacing(4),
  },

  '&& .rmdp-day': {
    width: theme.spacing(8),
    height: theme.spacing(8),
    margin: '2px',
  },

  '&& .rmdp-header': {
    height: 'auto',
    width: '100%',
    justifyContent: 'space-between',
    '& .rmdp-header-values': {
      padding: theme.spacing(2),
      '& > span': {
        paddingLeft: theme.spacing(2),
      },
    },
  },
}));

const CustomButtons = (
  direction: string,
  handleClick: React.MouseEventHandler<HTMLButtonElement> | undefined
) => {
  return (
    <StyledArrowButton onClick={handleClick}>
      <StyledArrow style={direction === 'right' ? { transform: 'rotate(180deg)' } : {}} />
    </StyledArrowButton>
  );
};

const StyledArrow = styled(IconCaretLeft)(() => ({
  height: '27px',
  width: '27px',
}));

const StyledArrowButton = styled('button')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  height: '39px',
  width: '39px',
  background: 'inherit',
  border: 'none',
  borderRadius: 8,
  padding: 0,

  '&:not(:disabled):hover': {
    background: theme.palette.lwPrimaryAccentLight[10],
    cursor: 'pointer',
    '& svg': {
      '& path': { stroke: theme.palette.lwSecondaryAccentLight[100] },
    },
  },
}));
